import GridColumn from '@navi/web-ui/lib/layouts/Grid/GridColumn/GridColumn';
import GridRow from '@navi/web-ui/lib/layouts/Grid/GridRow/GridRow';
import { BorderedInput, Button, Typography } from '@navi/web-ui/lib/primitives';
import React, { useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import NaviNewLogoIcon from '@navi/web-ui/lib/icons/NaviLogoIcon/NaviNewLogoIcon';
import { RootState } from '../../../../store';
import { LoginInputScreenConstants } from '../../constants/AuthConstants';
import styles from '../Login.module.scss';
import { sendOTP, signInWithGoogle, exchangeSession } from '../../AuthActions';
import { useNavigate } from 'react-router-dom';
import APP_ROUTES from 'src/layout/Routes';
import { LoginErrorMessage } from './ErrorMessage';
import { resetGlobalError, setGlobalError } from '../../reducers/authSlice';
import GoogleIcon from '../../../../assets/icons/GoogleIcon';
import { ExtensionHandler } from 'src/utils/extension.utils';
import { addClickstreamEvent } from '../../../../service/clickStreamEventService';
import { CLICKSTREAM_EVENT_NAMES } from '../../../../service/clickStream.constant';
import logger from '@cp/src/utils/logger';

const enableSSOFlag = window.config.ENABLE_SSO;
const MobileNumberInputScreen = () => {
  const dispatch = useDispatch();
  const getOTPError = useSelector((state: RootState) => state.auth.getOTPError);
  const isLoading = useSelector((state: RootState) => state.auth.isLoading);
  const mobileNumber = useSelector((state: RootState) => state.auth.mobileNumber);
  const [phoneNumber, setPhoneNumber] = React.useState<string>(mobileNumber || '');
  const [isPristine, setIsPristine] = React.useState<boolean>(true);
  const fcmToken = useSelector((state: RootState) => state?.common?.fcmToken) || '';

  const navigate = useNavigate();

  const handleInputChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>): void => {
      if (isPristine) setIsPristine(false);
      setPhoneNumber(event.target.value);
    },
    [phoneNumber]
  );

  useEffect(() => {
    dispatch(resetGlobalError());
    if (/\D/g.test(phoneNumber)) {
      const result = phoneNumber.replace(/\D/g, '');
      setPhoneNumber(result);
    }
  }, [phoneNumber]);

  const handleGetOTP = (event: React.SyntheticEvent): void => {
    logger('agent trying to login');
    event.preventDefault();
    ExtensionHandler.logout();
    dispatch(sendOTP({ phoneNumber }));
  };

  const supportedParams = ['state', 'code', 'error', 'errorDescription', 'errorUri'];

  const handleGoogleSignIn = (event: React.SyntheticEvent): void => {
    event.preventDefault();
    addClickstreamEvent(CLICKSTREAM_EVENT_NAMES.LH_Login_GoogleCTA, {});
    ExtensionHandler.logout();
    signInWithGoogle();
  };

  const handleMobileNumberError = useMemo(() => {
    if (phoneNumber.length < 10 && !isPristine) {
      return 'Enter 10 digit valid mobile number';
    } else if (getOTPError?.message) {
      return true;
    }
  }, [phoneNumber, getOTPError, isPristine]);

  const processOauthCallBack = () => {
    let code = '';
    let state = '';
    const callbackParams = decodedUri.substring(fragmentIndex + 1);
    const p = callbackParams.split('&');
    for (let i = 0; i < p.length; i++) {
      const split = p[i].indexOf('=');
      const key = p[i].slice(0, split);
      if (supportedParams.indexOf(key) !== -1) {
        if (key === 'code') {
          code = p[i].slice(split + 1);
        } else if (key === 'state') {
          state = p[i].slice(split + 1);
        }
      }
    }
    dispatch(exchangeSession(code, state, fcmToken));
  };

  const decodedUri = decodeURIComponent(window.location.href);
  const fragmentIndex = decodedUri.indexOf('#');
  useEffect(() => {
    dispatch(resetGlobalError());
    if (fragmentIndex !== -1) {
      processOauthCallBack();
    }
  }, []);

  return (
    <>
      <GridRow>
        <GridColumn md>
          <NaviNewLogoIcon size="lg" />
        </GridColumn>
      </GridRow>
      <GridRow>
        <GridColumn md>
          <Typography variant="h2" className={styles.title}>
            {LoginInputScreenConstants.title}
          </Typography>
          <Typography variant="p3" className={styles.subTitle}>
            {LoginInputScreenConstants.subTitle}
          </Typography>
        </GridColumn>
      </GridRow>
      {enableSSOFlag === 'true' && (
        <form onSubmit={handleGoogleSignIn}>
          <GridRow>
            <GridColumn md>
              <div className={styles['auth-wrapper__login-wrapper__bottom-cta']}>
                <Button
                  size="large"
                  type="submit"
                  fullWidth
                  variant="primary"
                  className={styles.googleButton}
                  disabled={isLoading}
                  startAdornment={
                    <div style={{ marginRight: '10px' }}>
                      <GoogleIcon />
                    </div>
                  }
                >
                  {isLoading
                    ? LoginInputScreenConstants.btnTextSigningIn
                    : LoginInputScreenConstants.btnTextSignInWithGoogle}
                </Button>
              </div>
            </GridColumn>
          </GridRow>
        </form>
      )}
      <GridRow>
        <GridColumn md>
          <p className={styles.orSeparator}>
            <span>or</span>
          </p>
        </GridColumn>
      </GridRow>
      <form onSubmit={handleGetOTP}>
        <GridRow>
          <GridColumn md>
            <Typography variant="p3" className={styles.subTitle}>
              {LoginInputScreenConstants.otpSubtitle}
            </Typography>
            <BorderedInput
              autoFocus
              inputSize="full-width"
              onChange={handleInputChange}
              error={handleMobileNumberError}
              value={phoneNumber}
              maxLength={10}
            />
          </GridColumn>
        </GridRow>
        <GridRow>
          <GridColumn md>
            <div className={styles['auth-wrapper__login-wrapper__bottom-cta']}>
              <Button
                size="large"
                type="submit"
                fullWidth
                variant="secondary"
                disabled={!(phoneNumber?.length === 10) || isLoading}
                className={styles.ctaTextColor}
              >
                {isLoading
                  ? LoginInputScreenConstants.btnTextSendingOTP
                  : LoginInputScreenConstants.btnTextSendOTP}
              </Button>
            </div>
          </GridColumn>
        </GridRow>
      </form>
      <LoginErrorMessage />
    </>
  );
};

export default MobileNumberInputScreen;
